import {
    BuildingOffice2Icon,
    ChatBubbleLeftIcon,
    ClipboardDocumentListIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { ChoiceTileOption, ChoiceTiles } from "components/ui/choice-tiles";
import { InlineBanner } from "components/ui/inline-banner";
import { useTaskNouns } from "hooks/use-task-nouns";
import React from "react";
import { handleEvent } from "util/user-events";
import { EmailSenderEditor } from "../email-action-editor";
import { entityTypeNoun } from "../manage-workflows-helper";
import { SmsActionEditor } from "../sms-action-editor";
import { TaskConfigurationEditor, TaskNotificationEditor } from "../task-action-editor";
import { EditableWorkflow } from "../types";
import { WorkflowSettingsField } from "../workflow-settings-field";
import { workflowEventProperties } from "./builder-analytics";
import {
    smsStepBlocker,
    WizardSectionEditor,
    WizardSectionItem,
    wizardStepKind,
    wizardStepSections,
    wizardStepSettings,
} from "./builder-helper";
import { SequenceStrip } from "./sequence-strip";
import { StepTimingEditor } from "./step-timing-editor";
import { WizardSection } from "./wizard-section";
import { WizardStepProps } from "./wizard-types";
import { stepKey, stepOptions, withStep } from "./workflow-blueprint";

type WorkflowSetting = EditableWorkflow["settings"][number];

const stepIcon = (key: string, className: string): React.ReactNode => {
    if (key === "task") {
        return <ClipboardDocumentListIcon className={className} />;
    }
    if (key === "sms") {
        return <ChatBubbleLeftIcon className={className} />;
    }
    if (key === "email:unselectedCommunities") {
        return <BuildingOffice2Icon className={className} />;
    }
    return <EnvelopeIcon className={className} />;
};

export const WizardSteps: React.FC<WizardStepProps> = props => {
    const {
        workflow,
        workflowSettings,
        editable,
        isDraft,
        canEditStructure,
        onChangeAction,
        editPayload,
        refetch,
        blueprint,
    } = props;

    const nouns = useTaskNouns();
    const stepKind = wizardStepKind(workflow.actions ?? []);
    const stepSettings = wizardStepSettings(workflow.settings || []);
    const sections = wizardStepSections(
        stepKind,
        stepSettings.map(setting => setting.key),
        { task: nouns, entity: entityTypeNoun(workflow.attachTo) },
    );

    const eventProperties = React.useMemo(
        () => workflowEventProperties(workflow, blueprint.blueprint),
        [workflow, blueprint.blueprint],
    );

    const trpcClient = trpc.useTRPC();
    const phoneLinesQuery = useQuery({
        ...trpcClient.telecom.listWorkflowPhoneLines.queryOptions({
            workflowId: workflow.id,
        }),
        enabled: stepKind === "sms",
    });
    const phoneLines = phoneLinesQuery.data?.phoneLines ?? [];
    const smsBlocker =
        stepKind === "sms"
            ? smsStepBlocker({
                  loaded: phoneLinesQuery.isSuccess,
                  phoneLineCount: phoneLines.length,
              })
            : null;

    const commitDelayRecipe = React.useCallback(
        (delayRecipe: string) => {
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "timing",
                delayRecipe,
            });
            editPayload(payload => ({ ...payload, delay: 0, delayRecipe }));
        },
        [editPayload, eventProperties],
    );

    const reportSettingChange = React.useCallback(
        (settingKey: string) => {
            handleEvent("workflow-builder-setting-change", {
                ...eventProperties,
                settingKey,
            });
            refetch();
        },
        [eventProperties, refetch],
    );

    const stepKindOptions = React.useMemo<ChoiceTileOption<string>[]>(() => {
        if (!blueprint.blueprint) {
            return [];
        }
        return stepOptions(blueprint.blueprint, blueprint.context.nouns).map(option => ({
            key: option.key,
            title: option.title,
            description: option.description,
            icon: stepIcon(option.key, "w-5 h-5"),
        }));
    }, [blueprint.blueprint, blueprint.context.nouns]);

    const handleStepKindSelect = React.useCallback(
        (key: string) => {
            if (!blueprint.blueprint) {
                return;
            }
            const option = stepOptions(blueprint.blueprint, blueprint.context.nouns).find(
                candidate => candidate.key === key,
            );
            if (option) {
                blueprint.onApply(withStep(blueprint.blueprint, option.step));
            }
        },
        [blueprint],
    );

    const renderEditor = (editor: WizardSectionEditor): React.ReactNode => {
        switch (editor) {
            case "step-kind":
                return (
                    <div className="space-y-4">
                        {canEditStructure && blueprint.blueprint ? (
                            <ChoiceTiles
                                options={stepKindOptions}
                                columns={2}
                                value={stepKey(blueprint.blueprint.step)}
                                onSelect={handleStepKindSelect}
                            />
                        ) : (
                            <SequenceStrip workflow={workflow} />
                        )}
                        {editable && !isDraft && blueprint.blueprint ? (
                            <p className="text-xs text-gray-400">
                                Unpublish this workflow to change what this step does.
                            </p>
                        ) : null}
                        <StepTimingEditor
                            workflow={workflow}
                            verb={timingVerb(stepKind)}
                            onCommit={commitDelayRecipe}
                        />
                    </div>
                );
            case "email-sender":
                return (
                    <EmailSenderEditor workflow={workflow} onChange={onChangeAction} />
                );
            case "sms-sender":
                return (
                    <div className="space-y-4">
                        {smsBlocker ? (
                            <InlineBanner type="warning">{smsBlocker}</InlineBanner>
                        ) : null}
                        <SmsActionEditor
                            workflow={workflow}
                            onChange={onChangeAction}
                            phoneLines={phoneLines}
                            phoneLinesLoaded={phoneLinesQuery.isSuccess}
                        />
                    </div>
                );
            case "task-details":
                return (
                    <TaskConfigurationEditor
                        workflow={workflow}
                        onChange={onChangeAction}
                    />
                );
            case "task-notification":
                return (
                    <TaskNotificationEditor
                        workflow={workflow}
                        onChange={onChangeAction}
                    />
                );
        }
    };

    const renderItem = (item: WizardSectionItem): React.ReactNode => {
        if (item.type === "editor") {
            return (
                <React.Fragment key={item.editor}>
                    {renderEditor(item.editor)}
                </React.Fragment>
            );
        }
        const setting = settingWithKey(stepSettings, item.settingKey);
        if (!setting) {
            return null;
        }
        return (
            <WorkflowSettingsField
                key={`${workflow.id}.tenant.${setting.key}`}
                workflow={workflow}
                setting={setting}
                label={item.label === "hidden" ? "" : setting.label}
                workflowSettings={workflowSettings.tenant}
                onSaved={() => reportSettingChange(setting.key)}
                locked={!!smsBlocker}
            />
        );
    };

    return (
        <div className="space-y-4">
            {sections.map((section, index) => (
                <WizardSection
                    key={section.key}
                    number={index + 1}
                    title={section.title}
                    description={section.description}>
                    {section.items.map(renderItem)}
                </WizardSection>
            ))}
        </div>
    );
};

const timingVerb = (stepKind: ReturnType<typeof wizardStepKind>): string => {
    switch (stepKind) {
        case "task":
            return "Create";
        case "email":
        case "sms":
            return "Send";
        case null:
            return "Run";
    }
};

const settingWithKey = (
    settings: WorkflowSetting[],
    key: string,
): WorkflowSetting | null => settings.find(setting => setting.key === key) ?? null;
